import { Injectable, InjectionToken, inject } from "@angular/core";

// 테마 (nova-common-themes.scss 의 html[data-np-theme="..."] 색 묶음)
export declare type NpThemeItem = {
    id: string,
    name: string,
    // 메뉴에 보여줄 대표 색 (점)
    color: string,
    // 다크 테마. Bootstrap 다크모드(data-bs-theme="dark")를 함께 켠다
    dark?: boolean,
};

// 화주사 테마 목록. 앱 설정에서 env 의 테마 목록을 넘긴다. 예) { provide: NP_THEME_ITEMS, useValue: environment.themes }
// 첫 번째 테마가 테마를 고른 적이 없을 때 적용할 테마다. 다크 테마는 dark: true 로 표시한다.
export const NP_THEME_ITEMS = new InjectionToken<NpThemeItem[]>('NP_THEME_ITEMS');

// 화주사 테마만 쓴다 (기본 테마를 빼고 NP_THEME_ITEMS 만 둔다). 예) 딜러 앱: { provide: NP_THEME_COMPANY_ONLY, useValue: true }
export const NP_THEME_COMPANY_ONLY = new InjectionToken<boolean>('NP_THEME_COMPANY_ONLY');

/**
 * 테마 변경
 * - 선택한 테마를 이 브라우저에 저장(localStorage)하고, 다음 접속 때도 그대로 적용한다. (그리드 컬럼 레이아웃 저장과 같은 방식)
 * - 테마 색은 nova-common-themes.scss 에서 html[data-np-theme="..."] 로 정의한다. 화주사 테마는 sls-nova-company-themes/nova-{화주사}-themes.scss 에서 정의한다.
 * - 화주사 테마 목록(NP_THEME_ITEMS)이 있으면 메뉴 맨 위에 두고, 테마를 고른 적이 없을 때 그 첫 번째 테마를 적용한다.
 * - NP_THEME_COMPANY_ONLY 가 true 면 기본 테마를 빼고 화주사 테마만 둔다. (딜러 앱)
 * - dark 로 표시한 테마는 다크 테마처럼 data-bs-theme="dark" 를 붙인다.
 * - 저장된 테마가 목록에 없으면 '기본' 테마를 쓴다. (화주사 테마만 쓰면 첫 번째 화주사 테마)
 */
@Injectable({ providedIn: 'root' })
export class NpThemeService {

    private readonly saveKey = 'np-theme';

    readonly themes: NpThemeItem[] = [
        { id: 'default', name: '기본', color: '#fff' }, // #0d6efd
        { id: 'blue', name: '블루', color: '#0c66e4' },
        { id: 'purple', name: '퍼플', color: '#6e5dc6' },
        { id: 'teal', name: '틸', color: '#227d9b' },
        { id: 'magenta', name: '마젠타', color: '#ae4787' },
        { id: 'dark', name: '다크', color: '#212529', dark: true },
    ];

    // 테마를 고른 적이 없을 때 적용할 테마 (화주사 테마가 있으면 화주사 테마 목록의 첫 번째)
    private readonly startTheme: string = 'default';

    private _current = 'default';
    public get current() {
        return this._current;
    }

    constructor() {
        // 화주사 테마를 메뉴 맨 위에 둔다 (id 가 없거나 이미 있는 테마는 무시). 첫 번째 화주사 테마가 시작 테마다.
        const companyThemes: NpThemeItem[] = [];
        for (const companyTheme of inject(NP_THEME_ITEMS, { optional: true }) ?? []) {
            if (companyTheme?.id && ![...companyThemes, ...this.themes].some(theme => theme.id === companyTheme.id)) {
                companyThemes.push(companyTheme);
            }
        }
        // 화주사 테마만 쓰면 기본 테마를 뺀다 (화주사 테마가 없으면 기본 테마를 그대로 둔다)
        if (inject(NP_THEME_COMPANY_ONLY, { optional: true }) && companyThemes.length) {
            this.themes.splice(0);
        }
        this.themes.unshift(...companyThemes);
        if (companyThemes.length) {
            this.startTheme = companyThemes[0].id;
        }

        // 저장된 테마를 바로 적용
        this.apply(localStorage.getItem(this.saveKey) || this.startTheme);
    }

    // 테마를 바꾸고 이 브라우저에 저장
    change(id: string) {
        this.apply(id);
        if (this._current === this.startTheme) {
            localStorage.removeItem(this.saveKey);
        } else {
            localStorage.setItem(this.saveKey, this._current);
        }
    }

    // 앱 화면(app-main)에 들어올 때 고른 테마를 html 에 다시 붙인다.
    attach() {
        this.apply(this._current);
    }

    // 앱 화면에서 나갈 때(로그인 화면 등) html 의 테마를 뗀다. 고른 테마와 저장값은 그대로 둔다.
    // 세션이 끝나 화면 이동만으로 로그인 화면에 가도 다크 등이 남지 않게 한다. (로그인 화면은 항상 밝게)
    detach() {
        const html = document.documentElement;
        html.removeAttribute('data-np-theme');
        html.removeAttribute('data-bs-theme');
    }

    private apply(id: string) {
        // 목록에 없으면 '기본' 테마 (화주사 테마만 쓰면 '기본'이 없으므로 시작 테마)
        const fallback = this.themes.some(theme => theme.id === 'default') ? 'default' : this.startTheme;
        this._current = this.themes.some(theme => theme.id === id) ? id : fallback;

        const html = document.documentElement;
        if (this._current === 'default') {
            html.removeAttribute('data-np-theme');
        } else {
            html.setAttribute('data-np-theme', this._current);
        }

        if (this.themes.find(theme => theme.id === this._current)?.dark) {
            html.setAttribute('data-bs-theme', 'dark');
        } else {
            html.removeAttribute('data-bs-theme');
        }
    }
}
